import { createElement, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { t } from 'i18n-js';

import FileInput from '~/components/atoms/FormControls/FileInput';
import Form from '~/components/atoms/FormControls/Form';
import Input from '~/components/atoms/FormControls/Input';
import Select from '~/components/atoms/FormControls/SelectExpandable';
import Textarea from '~/components/atoms/FormControls/Textarea';
import { ScrollContainer } from '~/components/atoms/ScrollContainer';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import { MenuViewHeader } from '~/components/MenuViewHeader';

import useOnClose from '~/hooks/useOnClose';
import {
  CONTACT_TOPICS,
  EContactFormField,
  EContactFormTopic,
  EContactInputs,
} from '~/popups/ContactPopup/consts';
import { useContactForm } from '~/popups/ContactPopup/useContactForm';

import styles from './ContactPopup.module.scss';

const CONTACT_FORM_ID = 'contactForm';

const COMPONENT_MAP = {
  [EContactInputs.SELECT]: Select,
  [EContactInputs.INPUT]: Input,
};

export function ContactPopup(): JSX.Element {
  const onClose = useOnClose();
  const formState = useContactForm();

  const { control, watch, resetField, userNameField, isSubmitEnabled } =
    formState;
  const selectedTopic = watch(EContactFormField.TOPIC);

  useEffect(() => {
    resetField(EContactFormField.SCREENSHOTS);
    resetField(EContactFormField.ADDITIONAL_INFO);
  }, [resetField, selectedTopic]);

  const { additionalField, attachmentText = '' } =
    CONTACT_TOPICS[selectedTopic] || {};
  return (
    <>
      <MenuViewHeader
        title={t('ContactView_header')}
        onClose={onClose}
      />
      <ScrollContainer
        flex
        resizable
      >
        <div className={styles.headline}>{t('ContactView_header')}</div>
        <Form
          id={CONTACT_FORM_ID}
          useFormReturn={formState}
          className={styles.container}
        >
          {userNameField && (
            <Input
              label={t('ContactView_label_name')}
              name={EContactFormField.USERNAME}
            />
          )}
          <Input
            label={t('ContactView_label_email')}
            placeholder={t('MenuView_email_placeholder')}
            name={EContactFormField.EMAIL}
            type="email"
          />
          <Controller
            control={control}
            name={EContactFormField.TOPIC}
            render={({
              field: { onChange, onBlur, value },
              fieldState: { error },
            }) => (
              <Select
                options={Object.values(EContactFormTopic)}
                label={t('ContactView_label_common_problems')}
                placeholder={t('ContactView_label_common_problems')}
                id={EContactFormField.TOPIC}
                onChange={onChange}
                onBlur={onBlur}
                value={value ? t(value) : ''}
                translateValue={false}
                error={error?.message}
              />
            )}
          />
          {additionalField?.Component &&
            COMPONENT_MAP[additionalField.Component] &&
            // @ts-ignore dunno
            createElement(COMPONENT_MAP[additionalField.Component], {
              label: t(additionalField.label),
              options: additionalField.options || [],
              name: EContactFormField.ADDITIONAL_INFO,
            })}
          <Textarea
            label={t('ContactView_label_message')}
            name={EContactFormField.MESSAGE}
            spellCheck
            rows={8}
          />
          {attachmentText && (
            <Controller
              control={control}
              name={EContactFormField.SCREENSHOTS}
              render={({
                field: { onChange, onBlur, value },
                fieldState: { error },
              }) => (
                <FileInput
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  label={t('ContactView_label_attachments')}
                  id={EContactFormField.SCREENSHOTS}
                  buttonTranslation={t('LootGemQuestView_screenshot_label')}
                  text={t(attachmentText)}
                  error={error?.message}
                />
              )}
            />
          )}
        </Form>
      </ScrollContainer>
      <div className={styles.buttonContainer}>
        <StyledButton
          variant={EStyledButtonVariant.BLUE}
          className={styles.button}
          type="submit"
          disabled={!isSubmitEnabled}
          form={CONTACT_FORM_ID}
        >
          {t('ContactView_submit_button')}
        </StyledButton>
      </div>
    </>
  );
}
